import React, { useEffect, useRef, useState } from 'react';
import { Box, Paper, Typography, Backdrop, CircularProgress } from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-enterprise';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import PF from "../_Services/publicFunction";
import { useSnackbar } from "notistack";
import Qs from "qs";
import { useSelector } from "react-redux";

const AnnualReportCustomer = () => {
  const [open, setOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const { UserInfo, token } = useSelector(state => ({
    UserInfo: state.app.UserInfo,
    token: state.app.token,
  }));

  const gridRef = useRef();
  const [columnDefs, setColumnDefs] = useState([]);
  const [rowData, setRowData] = useState([]);
  const [customerName, setCustomerName] = useState('');
  const [customerID, setCustomerID] = useState('');
  const [birthday, setBirthday] = useState('');
  const [age, setAge] = useState('');

  const defaultColDef = { resizable: true, sortable: true, filter: true };
  const rowHeight = 30;

  const fetchLock = useRef(false); // 🔹 防止重複 fetch

  const formatDate = (d) => {
    if (!d) return "";
    const parts = d.split(/[\/\-]/);
    if (parts.length < 3) return d;
    return `${parts[0]}/${parts[1].padStart(2,'0')}/${parts[2].padStart(2,'0')}`;
  };

  const fetchAnnualReport = async () => {
    if (fetchLock.current) return; // 已經在執行就跳過
    if (!UserInfo?.PersonalId || !token) return;

    fetchLock.current = true;
    setOpen(true);

    try {
      // 取得健檢日期 checkdate
      const statusRes = await PF.instance({
        method: "post",
        url: PF.url2 + "/Minmax/GetIDList_Customer",
        data: Qs.stringify({ Action: "1", parameter: { PersonalId: UserInfo.PersonalId } }),
        headers: { Authorization: "Bearer " + token }
      });

      const row3 = statusRes.data.rows || [];
      const checkDatesSet = new Set(row3.map(r => formatDate(r.checkdate)));

      if (checkDatesSet.size === 0) {
        enqueueSnackbar("無可查詢的報告", { variant: "warning" });
        setRowData([]);
        setColumnDefs([]);
        setCustomerName('');
        setCustomerID('');
        setBirthday('');
        setAge('');
        return;
      }

      // 取得年度報表
      const reportRes = await PF.instance({
        method: "post",
        url: PF.url2 + "/Minmax/GetAnnualReport_Customer",
        data: Qs.stringify({ Action: "1", parameter: { PersonalId: UserInfo.PersonalId } }),
        headers: { Authorization: "Bearer " + token }
      });

      const { rows = [] } = reportRes.data;

      // 過濾 rows，只保留 SpecialCheckDate 對應 checkdate 的
      const filteredRows = rows.filter(r => checkDatesSet.has(formatDate(r.SpecialCheckDate)));

      if (filteredRows.length === 0) {
        enqueueSnackbar("查無對應的報告資料", { variant: "warning" });
        setRowData([]);
        setColumnDefs([]);
        setCustomerName('');
        setCustomerID('');
        setBirthday('');
        setAge('');
        return;
      }

      // 設置基本資料（取第一筆）
      const firstRow = filteredRows[0];
      setCustomerName(firstRow.Name || '');
      setCustomerID(firstRow.id || '');
      setBirthday(firstRow.Birthday ? formatDate(firstRow.Birthday) : '');
      setAge(firstRow.Age || '');

      // 生成欄位
      const dates = Array.from(new Set(filteredRows.map(r => formatDate(r.SpecialCheckDate))))
        .sort((a,b) => new Date(b) - new Date(a));

      const cols = [
        { field: 'ItemName', headerName: '項目名稱', width: 230, pinned: 'left' },
        { field: 'Reference', headerName: '參考值', width: 240, pinned: 'left' },
      ];

      dates.forEach(dateStr => {
        cols.push({
          field: dateStr,
          headerName: dateStr,
          width: 270,
          cellClass: 'number-cell',
          cellStyle: params =>
            params.data?.ErrMap?.[dateStr]
              ? { color: '#d32f2f', fontWeight: 'bold' }
              : { color: '#000000' }
        });
      });

      setColumnDefs(cols);

      // 生成 rowData
      const itemMap = {};
      filteredRows.forEach(r => {
        const dateStr = formatDate(r.SpecialCheckDate);
        const itemKey = r.ItemName || r.projectno || r.barcodestr || `item-${Math.random()}`;
        const reference = r.Reference || '';

        if (!itemMap[itemKey]) itemMap[itemKey] = { ItemName: itemKey, Reference: reference, ErrMap: {} };
        itemMap[itemKey][dateStr] = r.Value || '';
        itemMap[itemKey].ErrMap[dateStr] = r.Err || false;
      });

      setRowData(Object.values(itemMap));

    } catch (err) {
      console.error(err);
      enqueueSnackbar("取得報告資料錯誤", { variant: "error" });
      setRowData([]);
      setColumnDefs([]);
    } finally {
      setOpen(false);
      fetchLock.current = false;
    }
  };

  useEffect(() => {
    fetchAnnualReport();
  }, [UserInfo?.PersonalId, token]); // 🔹 精準依賴

  return (
    <Box sx={{ p: 0.5, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {(customerName || customerID || birthday || age) && (
        <Paper sx={{ p: 1, mb: 1, backgroundColor: '#f0f0f0' }} elevation={2}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
            姓名：{customerName} &nbsp;&nbsp;&nbsp;
            身分證號：{customerID} &nbsp;&nbsp;&nbsp;
            生日：{birthday} &nbsp;&nbsp;&nbsp;
            年齡：{age}
          </Typography>
        </Paper>
      )}

      <Paper sx={{ height: '86vh', p: 1 }} elevation={3}>
        <div className="ag-theme-alpine" style={{ height: '86%', width: '100%' }}>
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            columnDefs={columnDefs}
            rowSelection="single"
            defaultColDef={defaultColDef}
            enableRangeSelection={true}
            rowHeight={rowHeight}
          />
        </div>
      </Paper>

      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={open}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
};

export default AnnualReportCustomer;
